import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { SOURCES, BRANCHES } from '../../config/constants';
import { getConsultancies } from '../../services/consultancyService';

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile',
  'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'DR Congo', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan',
  'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
  'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia',
  'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan',
  'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'UAE', 'Uganda', 'UK', 'Ukraine', 'Uruguay',
  'USA', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe', 'Other',
];

const INTAKE_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must not exceed 100 characters'),
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9+\-\s()]{7,15}$/, 'Enter a valid phone number'),
  email: yup.string().email('Enter a valid email address').nullable(),
  dob: yup.string().nullable(),
  course: yup
    .string()
    .required('Course is required')
    .min(2, 'Course must be at least 2 characters'),
  university: yup.string().nullable(),
  country: yup.string().required('Country is required'),
  intakeMonth: yup.string().required('Intake month is required'),
  loanAmount: yup
    .number()
    .typeError('Loan amount must be a number')
    .required('Loan amount is required')
    .positive('Loan amount must be a positive number'),
  interestedBank: yup.string().nullable(),
  source: yup.string().nullable(),
  consultancyId: yup.string().nullable(),
  consultancyPayout: yup
    .number()
    .typeError('Payout must be a number')
    .nullable()
    .transform((value, original) => (original === '' ? null : value)),
  branch: yup.string().nullable(),
});

const FormSection = ({ title, children }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Typography
        sx={{
          fontSize: '0.9375rem',
          fontWeight: 700,
          color: '#344050',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 2.5 }} />
      {children}
    </CardContent>
  </Card>
);

const StudentForm = ({ defaultValues = {}, onSubmit, loading = false }) => {
  const [consultancies, setConsultancies] = useState([]);

  useEffect(() => {
    getConsultancies()
      .then((res) => setConsultancies(res.data?.data?.consultancies || []))
      .catch(() => {});
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      dob: '',
      course: '',
      university: '',
      country: '',
      intakeMonth: '',
      loanAmount: '',
      interestedBank: '',
      source: '',
      consultancyId: '',
      consultancyPayout: '',
      branch: '',
      ...defaultValues,
    },
    resolver: yupResolver(validationSchema),
  });

  const selectedConsultancy = watch('consultancyId');

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Branch & Source Selection */}
      <FormSection title="Branch">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="branch"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Branch</InputLabel>
                  <Select {...field} label="Branch">
                    {BRANCHES.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="source"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select {...field} label="Source">
                    <MenuItem value="">
                      <em>Select source</em>
                    </MenuItem>
                    {SOURCES.map((source) => (
                      <MenuItem key={source} value={source}>
                        {source}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Personal Info Section */}
      <FormSection title="Personal Information">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Full Name"
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  autoFocus
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Phone Number"
                  required
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email Address"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.dob}
                  helperText={errors.dob?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Course Info Section */}
      <FormSection title="Course Information">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="course"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Course"
                  required
                  error={!!errors.course}
                  helperText={errors.course?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="university"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="University"
                  error={!!errors.university}
                  helperText={errors.university?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="country"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Autocomplete
                  {...field}
                  value={value || null}
                  onChange={(_, newValue) => onChange(newValue || '')}
                  options={COUNTRIES}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      error={!!errors.country}
                      helperText={errors.country?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="intakeMonth"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required error={!!errors.intakeMonth}>
                  <InputLabel>Intake Month</InputLabel>
                  <Select {...field} label="Intake Month">
                    {INTAKE_MONTHS.map((month) => (
                      <MenuItem key={month} value={month}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.intakeMonth && (
                    <FormHelperText>{errors.intakeMonth.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Loan Info Section */}
      <FormSection title="Loan Information">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="loanAmount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Loan Amount"
                  required
                  type="number"
                  error={!!errors.loanAmount}
                  helperText={errors.loanAmount?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">INR</InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="interestedBank"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Interested Bank"
                  error={!!errors.interestedBank}
                  helperText={errors.interestedBank?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="consultancyId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Consultancy</InputLabel>
                  <Select {...field} label="Consultancy">
                    <MenuItem value="">
                      <em>No consultancy</em>
                    </MenuItem>
                    {consultancies.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          {selectedConsultancy && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="consultancyPayout"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Consultancy Payout"
                    type="number"
                    error={!!errors.consultancyPayout}
                    helperText={errors.consultancyPayout?.message}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">INR</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Student'}
        </Button>
      </Box>
    </Box>
  );
};

export default StudentForm;
